import { FormattedNumberValue, FormattedNumberConfig } from '@core/types/models';
import React, { FunctionComponent, useState } from 'react';

interface FormattedNumberProps {
  id: string;
  value?: FormattedNumberValue;
  placeholder?: string;
  onChange: (value: FormattedNumberValue) => void;
  valid?: boolean;
  formats: FormattedNumberConfig[];
  setFormat?: (currency: FormattedNumberConfig) => void;
  selectedFormat?: FormattedNumberConfig;
}

export const formatNumber = (rawValue?: number, selectedFormat?: FormattedNumberConfig, trailingDecimal?: boolean) => {
  if (!rawValue || !selectedFormat) {
    return '';
  }

  const showDecimal = trailingDecimal || rawValue % 1 !== 0;
  const decimalsToShow = showDecimal
    ? // 2 Decimals for currency, otherwise show between 1 and 2 based on actual value
      selectedFormat.isCurrency
      ? 2
      : Math.min(rawValue.toString().split('.')[1]?.length || 0, 2)
    : 0;

  let numberStr = rawValue.toFixed(decimalsToShow);

  const parts = numberStr.split('.');
  let integerPart = parts[0];
  let decimalPart = parts[1] || '';

  if (selectedFormat.thousandSeparator) {
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, selectedFormat.thousandSeparator);
  }

  const decimalSymbol = selectedFormat.decimalSymbol || '.';
  numberStr = decimalPart ? `${integerPart}${decimalSymbol}${decimalPart}` : integerPart;

  let formattedValue = selectedFormat.value.replace('[[x]]', numberStr);

  return formattedValue;
};

const FormattedNumber: FunctionComponent<FormattedNumberProps> = ({
  id,
  value = null,
  placeholder,
  onChange,
  valid = true,
  selectedFormat,
  formats,
  setFormat,
}) => {
  if (!selectedFormat) {
    selectedFormat = formats[0];
  }

  const [isFocused, setIsFocused] = useState(false);
  const [trailingDecimal, setTrailingDecimal] = useState(false);
  const [inputValue, setInputValue] = useState(value?.rawValue?.toString() || '');

  const handleFormatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFormat = formats.find((format) => format.key === e.target.value);
    if (setFormat && newFormat !== undefined) {
      setFormat(newFormat);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const rawValue = parseFloat(inputValue);

    if (!isNaN(rawValue)) {
      const formattedValue = formatNumber(rawValue, selectedFormat, trailingDecimal);
      onChange({ rawValue, formattedValue });
    } else {
      onChange({ rawValue: undefined, formattedValue: '' });
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const displayValue = isFocused ? inputValue : value?.rawValue ? formatNumber(value.rawValue, selectedFormat, trailingDecimal) : '';

  const isPlaceholderShown = !displayValue;

  return (
    <div className={`form__row ${!valid ? 'invalid' : ''}`}>
      <div className="form__formatted-number" data-placeholder-shown={isPlaceholderShown ? 'true' : 'false'}>
        {formats && formats.length > 1 ? (
          <select
            id={`${id}-format`}
            value={selectedFormat.key}
            onChange={(e) => handleFormatChange(e)}
            className="form__control form__control--select"
          >
            {formats.map(({ key }) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </select>
        ) : (
          <div className="form__control--icon">{formats[0]?.key || ''}</div>
        )}
        <input
          type={isFocused ? 'number' : 'text'}
          step="0.01"
          id={id}
          placeholder={placeholder}
          className="form__control"
          value={displayValue}
          onChange={(e) => handleInputChange(e)}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
        {selectedFormat?.isCurrency && (
          <>
            <label htmlFor={`${id}-trailing-decimal`}>Trailing decimal</label>
            <input
              id={`${id}-trailing-decimal`}
              type="checkbox"
              onChange={() => setTrailingDecimal(!trailingDecimal)}
              checked={trailingDecimal}
            />
          </>
        )}
        <label htmlFor={id} className="form__label">
          {placeholder}
        </label>
      </div>
    </div>
  );
};

export default FormattedNumber;
